<template>
  <div
    v-if="embedded"
    class="carrossel-preview-embedded"
    :class="{ 'carrossel-preview-embedded--dark': darkEnabled }"
  >
    <h4 v-if="showTitle" class="preview-section-title">Pré-visualização</h4>

    <div v-if="hideBannerPreview" class="preview-placeholder">
      <v-icon large color="grey">fas fa-th-large</v-icon>
      <p>Preview de produtos disponível na vitrine publicada.</p>
    </div>

    <div v-else-if="!hasSlides" class="preview-placeholder">
      <v-icon large color="grey">fas fa-images</v-icon>
      <p>Adicione banners na etapa anterior para visualizar o carrossel.</p>
    </div>

    <div v-else class="container-carrossel container-carrossel--embedded">
      <h4
        v-if="shouldShowCarouselTitle && carouselTitle"
        class="carousel-title"
      >
        {{ carouselTitle }}
      </h4>
      <VueSlickCarousel
        :key="carouselKey"
        v-bind="slickOptions"
        class="carrossel"
      >
        <template v-if="showArrows" #prevArrow>
          <div class="custom-arrow prev-arrow">
            <i :class="arrowClasses[0]" :style="{ color: fontColor }" />
          </div>
        </template>
        <template v-if="showArrows" #nextArrow>
          <div class="custom-arrow next-arrow">
            <i :class="arrowClasses[1]" :style="{ color: fontColor }" />
          </div>
        </template>
        <div
          v-for="(slide, index) in displaySlides"
          :key="slide.id || slide.image || index"
        >
          <img
            :src="getSlideImageUrl(slide, slideImageRevisions)"
            alt=""
            class="imagem-container"
          />
        </div>
      </VueSlickCarousel>
    </div>
  </div>

  <v-dialog
    v-else
    v-model="internalPreview"
    @input="closeDialog"
    :width="selectedTamanhoTela"
    :dark="darkEnabled"
    content-class="carrossel-preview-dialog"
  >
    <v-card :dark="darkEnabled">
      <v-card-title class="text-h5 carrossel-dialog-title">
        Pré-visualização - "{{ titulo }}"
      </v-card-title>

      <v-card-text>
        <div class="container-carrossel">
          <h4
            v-if="shouldShowCarouselTitle && carouselTitle"
            class="carousel-title"
          >
            {{ carouselTitle }}
          </h4>
          <FormSelect
            label="Selecione o tamanho da tela"
            class="selectedTamanhoTela"
            v-model="selectedTamanhoTela"
            :options="screenOptions"
            :clearable="false"
          />

          <VueSlickCarousel
            :key="carouselKey"
            v-bind="slickOptions"
            class="carrossel"
          >
            <template v-if="showArrows" #prevArrow>
              <div class="custom-arrow prev-arrow">
                <i :class="arrowClasses[0]" :style="{ color: fontColor }" />
              </div>
            </template>
            <template v-if="showArrows" #nextArrow>
              <div class="custom-arrow next-arrow">
                <i :class="arrowClasses[1]" :style="{ color: fontColor }" />
              </div>
            </template>
            <div
              v-for="(slide, index) in displaySlides"
              :key="slide.id || slide.image || index"
            >
              <img
                :src="getSlideImageUrl(slide, slideImageRevisions)"
                :style="{ 'max-width': selectedTamanhoTela }"
                class="imagem-container"
                alt=""
              />
            </div>
          </VueSlickCarousel>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="closeDialog">
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VueSlickCarousel from "vue-slick-carousel";
import FormSelect from "@/components/form/FormSelect";
import { mapGetters } from "vuex";
import {
  applyNavigationPreset,
  getSlideImageUrl
} from "../../utils/utilCarrossel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
import "vue-slick-carousel/dist/vue-slick-carousel-theme.css";

export default {
  components: {
    VueSlickCarousel,
    FormSelect
  },
  props: {
    preview: {
      type: Boolean,
      default: false
    },
    slides: {
      type: Array,
      default: () => []
    },
    edit: {
      type: Object,
      default: null
    },
    appearanceState: {
      type: Object,
      default: null
    },
    carrossel: {
      type: Object,
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    },
    hideBannerPreview: {
      type: Boolean,
      default: false
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    slideImageRevisions: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    previewSource() {
      if (this.edit) {
        return this.edit;
      }
      if (this.appearanceState && this.carrossel) {
        return {
          selectedNavStyle: this.appearanceState.selectedNavStyle,
          autoplaySpeed: this.appearanceState.autoplayEnabled
            ? this.appearanceState.autoplaySpeed
            : 0,
          fade: this.appearanceState.fade,
          centerMode: this.appearanceState.centerMode,
          slidesToShowResponsive: this.appearanceState.slidesToShowResponsive,
          selectedDevice: this.appearanceState.selectedDevice,
          navigationPreset: this.appearanceState.navigationPreset,
          carrossel: this.carrossel
        };
      }
      return null;
    },
    displaySlides() {
      return (this.slides || []).filter(slide => slide?.image);
    },
    hasSlides() {
      return this.displaySlides.length > 0;
    },
    fontColor() {
      const config = this.previewSource?.carrossel?.config || {};
      return config.colors?.font || "#000000FF";
    },
    shouldShowCarouselTitle() {
      if (this.appearanceState?.showTitle != null) {
        return this.appearanceState.showTitle === true;
      }
      return this.previewSource?.carrossel?.config?.showTitle === true;
    },
    carouselTitle() {
      return this.previewSource?.carrossel?.titulo || "";
    },
    navigationConfig() {
      const config = { ...(this.previewSource?.carrossel?.config || {}) };
      if (this.previewSource?.navigationPreset) {
        applyNavigationPreset(this.previewSource.navigationPreset, config);
      }
      return config;
    },
    showArrows() {
      return this.navigationConfig.showArrows !== false;
    },
    showDots() {
      return this.navigationConfig.showDots !== false;
    },
    arrowClasses() {
      return this.obterPrevArrowClasses(
        this.previewSource?.selectedNavStyle || { name: "chevron" }
      );
    },
    carouselKey() {
      const src = this.previewSource;
      if (!src) return "empty";
      return [
        src.navigationPreset,
        src.autoplaySpeed,
        src.fade,
        src.centerMode,
        src.slidesToShowResponsive?.desktop,
        src.slidesToShowResponsive?.mobile,
        this.showArrows,
        this.showDots,
        this.fontColor,
        this.shouldShowCarouselTitle,
        this.carouselTitle,
        this.displaySlides.length,
        this.selectedTamanhoTela
      ].join("-");
    }
  },
  data() {
    return {
      internalPreview: this.preview,
      titulo: "",
      selectedTamanhoTela: "1280px",
      slickOptions: {},
      screenOptions: [
        { text: "Sem ação", value: "1280px" },
        { text: "740px", value: "740px" },
        { text: "320px", value: "320px" }
      ]
    };
  },
  created() {
    this.refreshSlickOptions();
  },
  methods: {
    getSlideImageUrl,
    closeDialog() {
      this.internalPreview = false;
      this.$emit("update:preview", false);
    },
    obterPrevArrowClasses(selectedNavStyle) {
      let prevArrowClass = "slider-arrow-left ";
      let nextArrowClass = "slider-arrow-right ";
      const name = selectedNavStyle?.name || "chevron";

      switch (name) {
        case "angleDouble":
          prevArrowClass += "fas fa-angle-double-left";
          nextArrowClass += "fas fa-angle-double-right";
          break;
        case "chevron":
          prevArrowClass += "fas fa-chevron-left";
          nextArrowClass += "fas fa-chevron-right";
          break;
        case "carret":
          prevArrowClass += "fas fa-caret-left";
          nextArrowClass += "fas fa-caret-right";
          break;
        case "chevronCircle":
          prevArrowClass += "fas fa-chevron-circle-left";
          nextArrowClass += "fas fa-chevron-circle-right";
          break;
        case "arrowCircle":
          prevArrowClass += "fas fa-arrow-circle-left";
          nextArrowClass += "fas fa-arrow-circle-right";
          break;
        default:
          prevArrowClass += "fas fa-chevron-left";
          nextArrowClass += "fas fa-chevron-right";
      }
      return [prevArrowClass, nextArrowClass];
    },
    refreshSlickOptions() {
      const edit = this.previewSource;
      if (!edit?.carrossel) {
        this.slickOptions = {};
        return;
      }

      this.titulo = edit.carrossel.titulo || "";

      const autoplay = Number(edit.autoplaySpeed) > 0;
      const responsive = edit.slidesToShowResponsive || {
        desktop: 3,
        mobile: 1
      };

      let slidesToShow = edit.fade ? 1 : Number(responsive.desktop) || 3;
      let centerMode = edit.centerMode === true;

      if (this.selectedTamanhoTela === "320px") {
        slidesToShow = Number(responsive.mobile) || 1;
        centerMode = slidesToShow === 1 ? centerMode : false;
      }

      let initialSlide = Number(edit.carrossel.ordem_area) - 1;
      if (initialSlide >= this.displaySlides.length) {
        initialSlide = Math.max(this.displaySlides.length - 1, 0);
      }

      this.slickOptions = {
        initialSlide,
        mobileFirst: edit.selectedDevice?.mobile === true,
        slidesToScroll: 1,
        slidesToShow: Math.min(slidesToShow, this.displaySlides.length || 1),
        arrows: this.showArrows,
        dots: this.showDots,
        fade: edit.fade === true,
        centerPadding: "10px",
        centerMode,
        autoplay,
        autoplaySpeed: edit.autoplaySpeed || 5000,
        dotsClass: this.navigationConfig.minimalDots
          ? "slick-dots slick-dots-minimal"
          : "slick-dots",
        responsive: [
          {
            breakpoint: 1280,
            settings: {
              slidesToShow: Math.min(
                slidesToShow,
                this.displaySlides.length || 1
              )
            }
          },
          {
            breakpoint: 740,
            settings: {
              slidesToShow: Math.min(
                Number(responsive.mobile) || 1,
                this.displaySlides.length || 1
              )
            }
          }
        ]
      };
    }
  },
  watch: {
    preview(newVal) {
      this.internalPreview = newVal;
    },
    selectedTamanhoTela() {
      this.refreshSlickOptions();
    },
    previewSource: {
      deep: true,
      handler() {
        this.refreshSlickOptions();
      }
    },
    slides: {
      deep: true,
      handler() {
        this.refreshSlickOptions();
      }
    }
  }
};
</script>

<style scoped>
.imagem-container {
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  width: 100%;
}

.custom-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(0, 0, 0);
  cursor: pointer;
  font-size: 20px;
  background-color: white;
  border-radius: 50%;
}

.custom-arrow.prev-arrow::before,
.custom-arrow.next-arrow::before {
  content: "" !important;
}

.custom-arrow.slick-prev {
  left: 20px;
  z-index: 1;
}

.custom-arrow.slick-next {
  right: 20px !important;
}

.container-carrossel {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.container-carrossel--embedded {
  margin-bottom: 0;
  background: transparent;
  border-radius: 0;
  padding: 16px 16px 40px;
  min-height: 200px;
  overflow: visible;
}

.carrossel {
  width: 98%;
}

.custom-arrow.slick-next,
.custom-arrow.slick-prev {
  width: 30px !important;
  height: 30px !important;
}

.selectedTamanhoTela {
  width: 98%;
  margin-top: 20px;
}

.preview-section-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.carousel-title {
  width: 100%;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 12px;
  padding: 0 8px;
  text-align: left;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: transparent;
  border-radius: 0;
  color: #888;
  text-align: center;
  padding: 24px;
}

.preview-placeholder p {
  margin-top: 12px;
  margin-bottom: 0;
}

.carrossel-preview-embedded--dark .preview-section-title {
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-preview-embedded--dark .carousel-title {
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-preview-embedded--dark .preview-placeholder {
  color: rgba(255, 255, 255, 0.65);
}

.carrossel-preview-embedded--dark .custom-arrow {
  background-color: #2a2a2a !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
}
</style>

<style>
.slick-dots-minimal li button:before {
  font-size: 8px !important;
}
</style>
